import { Typography, Alert } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useMemo, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { COLORS } from '../../../utils/Application_Constants';
import ExpenseReceiptUpload from './ExpenseReceiptUpload';
import { ExpenseStatusValuesEnum } from './ExpenseStatus.enum';
import { BillTypeEnum } from './BillType.enum';
import { ExpenseModeEnum } from './ExpenseMode.enum';

const useStyles = makeStyles(() => ({
    formHeader: {
        borderBottom: `1px solid ${COLORS.LT_MIDNIGHT25}`,
        paddingBottom: 8,
        marginBottom: 16,
    },
    asterisk: {
        color: COLORS.LT_PEPPER,
    },
    standardInfo: {
        background: COLORS.LT_MIDNIGHTSTEEL,
        color: COLORS.LT_MIDNIGHT75,
        fontSize: 12,
        lineHeight: 1.25,
        opacity: 0.8,
    },
    standardInfoIcon: {
        color: COLORS.LT_MIDNIGHT75,
    },
    internalNotesHeader: {
        marginTop: 32,
    },
    textError: {
        color: COLORS.LT_PEPPER,
    },
}));

const ExpenseReceiptUploadForm = (props) => {
    const {
        index,
        expense,
        originalExpenseEntries,
        mode,
        isViewOnly,
        onOCRUpload,
        expenseId,
        saveAndRegenerateVaultPdf,
        pdfSuccessMessage,
        setPdfSuccessMessage,
    } = props;

    const classes = useStyles();
    const { watch, errors } = useFormContext();
    const bookingBillType = watch(`expenseEntries[${index}].bookingBillType`);
    const billType = watch(`expenseEntries[${index}].billType`);
    const expenseStatus = watch(`expenseEntries[${index}].status`);
    const [formError, setFormError] = React.useState(false);
    const isRequired = useMemo(() => {
        const currentBillType = bookingBillType ?? billType;
        if (mode === ExpenseModeEnum.Payment) {
            return (
                currentBillType === BillTypeEnum.Billable ||
                currentBillType === BillTypeEnum.ClinicianDeduction
            );
        }
        if (mode === ExpenseModeEnum.Vault) {
            return (
                currentBillType === BillTypeEnum.Billable &&
                expenseStatus === ExpenseStatusValuesEnum.Approved
            );
        }

        if (mode === ExpenseModeEnum.CreditCard) {
            return currentBillType === BillTypeEnum.Billable;
        }

        return (
            expense?.status !== ExpenseStatusValuesEnum.Abandoned &&
            expense?.status !== ExpenseStatusValuesEnum.NonReimbursable &&
            expense?.status !== ExpenseStatusValuesEnum.NeedMoreInformation
        );
    }, [bookingBillType, billType, mode, expenseStatus]);
    // Get the original status from the originalExpenseEntries
    const originalStatus = useMemo(() => {
        if (expense.id && originalExpenseEntries?.length > 0) {
            const originalExpense = originalExpenseEntries.find((entry) => entry.id === expense.id);
            return originalExpense ? originalExpense.status : expense.status;
        }
        return expense.status; // Fallback to current status if no original entry is found
    }, [expense.id, originalExpenseEntries, expense.status]);
    // Track the current status being edited
    const currentStatus = watch(`expenseEntries[${index}].status`);
    // Compute isPosted based on original status and current transient state
    const isPosted = useMemo(
        () =>
            originalStatus === ExpenseStatusValuesEnum.Posted ||
            (currentStatus === ExpenseStatusValuesEnum.OnHoldReimbursement &&
                originalStatus === ExpenseStatusValuesEnum.Posted),
        [originalStatus, currentStatus]
    );
    const isPendingSubmission = expense.status === ExpenseStatusValuesEnum.PendingSubmission;
    const isAbandoned = currentStatus === ExpenseStatusValuesEnum.Abandoned;
    const isTimesheetDocumentUpload =
        watch('type') &&
        watch('category') &&
        watch('category') === 'Travel' &&
        watch('type') === 'Mileage';
    const sectionLabel = isTimesheetDocumentUpload
        ? 'Timesheet & Document Upload'
        : 'Receipt Upload';

    // Check if this is a payment request (supplemental payments)
    const isPaymentRequest = mode === ExpenseModeEnum.Payment;

    useEffect(() => {
        console.log('Errors updated in ExpenseReceiptUploadForm:', errors.fileUploads);
        if (
            errors?.fileUploads?.[index]?.newFiles ||
            errors?.fileUploads?.[index]?.existingFiles ||
            errors?.fileUploads?.[index]?.invoice.newFiles ||
            errors?.fileUploads?.[index]?.invoice.existingFiles
        ) {
            return setFormError(true);
        }

        return setFormError(false);
    }, [errors]);

    return (
        <div>
            {isPaymentRequest ? (
                // Dual upload sections for payment requests
                <div>
                    {/* Main Invoice Attachment Heading */}
                    <Typography variant="h5" component="h5" className={classes.formHeader}>
                        Invoice Attachment
                    </Typography>

                    {/* Info Alert */}
                    <Alert
                        severity="info"
                        icon={
                            <InfoOutlinedIcon
                                fontSize="small"
                                className={classes.standardInfoIcon}
                            />
                        }
                        className={classes.tip}
                        classes={{ standardInfo: classes.standardInfo }}
                    >
                        Please ensure images are clear, well-lit, free of blur or distortion, and in
                        upright orientation.
                    </Alert>

                    {/* Invoice Attachment Section */}
                    <ExpenseReceiptUpload
                        index={index}
                        mode={mode}
                        expense={expense}
                        isPosted={isPosted}
                        isPendingSubmission={isPendingSubmission}
                        isViewOnly={isViewOnly || isAbandoned}
                        onOCRUpload={onOCRUpload}
                        expenseId={expenseId}
                        attachmentType="invoice"
                        isInternal={false}
                        showSectionTitle
                        sectionTitle="Attachments for clients"
                        sectionRequired={isRequired}
                        saveAndRegenerateVaultPdf={saveAndRegenerateVaultPdf}
                    />
                </div>
            ) : (
                // Original single upload section for non-payment modes
                <div>
                    <Typography
                        variant="h5"
                        component="h5"
                        className={[classes.formHeader, formError ? classes.textError : ''].join(
                            ' '
                        )}
                    >
                        {sectionLabel}
                        {isRequired && <span className={classes.asterisk}> *</span>}
                    </Typography>
                    <Alert
                        severity="info"
                        icon={
                            <InfoOutlinedIcon
                                fontSize="small"
                                className={classes.standardInfoIcon}
                            />
                        }
                        className={classes.tip}
                        classes={{ standardInfo: classes.standardInfo }}
                    >
                        Please ensure images are clear, well-lit, free of blur or distortion, and in
                        upright orientation.
                    </Alert>
                    <ExpenseReceiptUpload
                        index={index}
                        mode={mode}
                        expense={expense}
                        isPosted={isPosted}
                        isPendingSubmission={isPendingSubmission}
                        isViewOnly={isViewOnly || isAbandoned}
                        onOCRUpload={onOCRUpload}
                        expenseId={expenseId}
                        attachmentType="standard"
                        isInternal={false}
                        showSectionTitle={false}
                        saveAndRegenerateVaultPdf={saveAndRegenerateVaultPdf}
                        pdfSuccessMessage={pdfSuccessMessage}
                        setPdfSuccessMessage={setPdfSuccessMessage}
                        sectionRequired={isRequired}
                    />
                </div>
            )}
        </div>
    );
};

export default ExpenseReceiptUploadForm;
